import { z } from "zod";
export const ScheduleType = z.enum(["ONCE", "RECURRING", "CHAINED"]);
export const Frequency = z.enum([
    "HOURLY",
    "DAILY",
    "WEEKLY",
    "MONTHLY",
    "CUSTOM",
]);
export const MonthlyMode = z.enum([
    "DAY_OF_MONTH",
    "LAST_DAY",
    "DAYS_BEFORE_END",
    "NTH_WEEKDAY",
]);
// Day-of-week scalar — 0 = Sunday, 6 = Saturday.
const DayOfWeek = z.number().int().min(0).max(6);
const Hour = z.number().int().min(0).max(23);
const cronOrEmpty = z.string().min(0).max(120);
// Structured advanced scheduling — the UI builds cron from these; the engine
// just reads cronExpression. All fields optional; null/empty falls back to
// the start-time-derived defaults the original implementation used.
const AdvancedFields = {
    // WEEKLY selection, and a day-restriction for DAILY/intra-day.
    weeklyDaysOfWeek: z.array(DayOfWeek).optional(),
    // MONTHLY mode + supporting values.
    monthlyMode: MonthlyMode.nullable().optional(),
    monthlyDayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
    monthlyDaysBeforeEnd: z.number().int().min(0).max(30).nullable().optional(),
    monthlyWeekday: DayOfWeek.nullable().optional(),
    // 1-5 = nth occurrence; -1 = last.
    monthlyNth: z
        .number()
        .int()
        .refine((v) => v === -1 || (v >= 1 && v <= 5), {
        message: "monthlyNth must be 1-5 or -1 (last)",
    })
        .nullable()
        .optional(),
    // DAILY intra-day repetition.
    intradayIntervalMinutes: z
        .number()
        .int()
        .min(1)
        .max(720)
        .nullable()
        .optional(),
    intradayStartHour: Hour.nullable().optional(),
    intradayEndHour: Hour.nullable().optional(),
};
// CHAINED-mode fields — point to the parent app whose successful reload
// triggers this schedule. All three live or die together at write time.
const ChainFields = {
    chainAfterConnectionId: z.number().int().positive().nullable().optional(),
    chainAfterAppId: z.string().min(1).nullable().optional(),
    chainAfterAppName: z.string().min(1).nullable().optional(),
};
// Body for POST /api/schedules. Server computes canonical cronExpression and
// initial nextRun (or leaves nextRun null for CHAINED).
export const ScheduleCreateBody = z
    .object({
    scheduleName: z.string().min(1).max(200),
    connectionId: z.number().int().positive(),
    appId: z.string().min(1),
    appName: z.string().min(1),
    scheduleType: ScheduleType,
    frequency: Frequency.optional(), // required when scheduleType=RECURRING
    cronExpression: cronOrEmpty.optional(), // required when frequency=CUSTOM
    customIntervalMinutes: z
        .number()
        .int()
        .min(1)
        .max(60 * 24 * 30)
        .optional(),
    // Optional at the zod level — required for ONCE / RECURRING via the
    // superRefine below. CHAINED schedules don't need a clock anchor, so
    // accepting the field as absent for them keeps the UI clean.
    startTime: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    timezone: z.string().min(1).default("UTC"),
    partialReload: z.boolean().default(false),
    retryEnabled: z.boolean().default(true),
    maxRetryAttempts: z.number().int().min(1).max(20).default(3),
    retryDelayMinutes: z.number().int().min(1).max(1440).default(5),
    exponentialBackoff: z.boolean().default(false),
    notifyOnFailure: z.boolean().default(false),
    notifyEmails: z.array(z.string().email()).default([]),
    apiKey: z.string().min(20).max(4096).optional(),
    ...AdvancedFields,
    ...ChainFields,
})
    .superRefine((v, ctx) => {
    if (v.scheduleType === "RECURRING" && !v.frequency) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["frequency"],
            message: "frequency is required when scheduleType=RECURRING",
        });
    }
    if (v.scheduleType === "RECURRING" &&
        v.frequency === "CUSTOM" &&
        !v.cronExpression &&
        !v.customIntervalMinutes) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["cronExpression"],
            message: "Either cronExpression or customIntervalMinutes is required when frequency=CUSTOM",
        });
    }
    // startTime is required for clock-based schedules but irrelevant for
    // event-driven CHAINED schedules (the server defaults to NOW() at write).
    if (v.scheduleType !== "CHAINED" && !v.startTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["startTime"],
            message: "startTime is required for ONCE and RECURRING schedules",
        });
    }
    if (v.scheduleType === "CHAINED") {
        if (!v.chainAfterConnectionId || !v.chainAfterAppId || !v.chainAfterAppName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["chainAfterAppId"],
                message: "chainAfterConnectionId + chainAfterAppId + chainAfterAppName are required for CHAINED schedules",
            });
        }
        if (v.chainAfterConnectionId === v.connectionId &&
            v.chainAfterAppId === v.appId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["chainAfterAppId"],
                message: "A schedule cannot chain off its own app",
            });
        }
    }
    // Intra-day window sanity.
    if (v.intradayStartHour != null &&
        v.intradayEndHour != null &&
        v.intradayStartHour > v.intradayEndHour) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["intradayEndHour"],
            message: "intradayEndHour must be ≥ intradayStartHour",
        });
    }
});
export const ScheduleUpdateBody = z
    .object({
    scheduleName: z.string().min(1).max(200).optional(),
    appName: z.string().min(1).optional(),
    scheduleType: ScheduleType.optional(),
    frequency: Frequency.optional(),
    cronExpression: cronOrEmpty.optional(),
    customIntervalMinutes: z.number().int().min(1).optional(),
    startTime: z.string().datetime().optional(),
    endDate: z.string().datetime().nullable().optional(),
    timezone: z.string().optional(),
    partialReload: z.boolean().optional(),
    retryEnabled: z.boolean().optional(),
    maxRetryAttempts: z.number().int().min(1).max(20).optional(),
    retryDelayMinutes: z.number().int().min(1).max(1440).optional(),
    exponentialBackoff: z.boolean().optional(),
    notifyOnFailure: z.boolean().optional(),
    notifyEmails: z.array(z.string().email()).optional(),
    apiKey: z.string().min(20).max(4096).nullable().optional(),
    isActive: z.boolean().optional(),
    ...AdvancedFields,
    ...ChainFields,
})
    .strict();
export const ScheduleView = z.object({
    id: z.number().int(),
    scheduleId: z.string(),
    scheduleName: z.string(),
    connectionId: z.number().int(),
    connectionName: z.string(),
    appId: z.string(),
    appName: z.string(),
    scheduleType: ScheduleType,
    frequency: Frequency.nullable(),
    cronExpression: z.string(),
    customIntervalMinutes: z.number().int().nullable(),
    startTime: z.string().datetime(),
    endDate: z.string().datetime().nullable(),
    timezone: z.string(),
    partialReload: z.boolean(),
    retryEnabled: z.boolean(),
    maxRetryAttempts: z.number().int(),
    retryDelayMinutes: z.number().int(),
    exponentialBackoff: z.boolean(),
    isActive: z.boolean(),
    isSuspended: z.boolean(),
    suspendedAt: z.string().datetime().nullable(),
    suspendedReason: z.string().nullable(),
    lastRun: z.string().datetime().nullable(),
    nextRun: z.string().datetime().nullable(),
    lastError: z.string().nullable(),
    consecutiveFailures: z.number().int(),
    notifyOnFailure: z.boolean(),
    notifyEmails: z.array(z.string()),
    hasPerScheduleApiKey: z.boolean(),
    // Advanced + chain (echo back so the UI can re-render the create form).
    weeklyDaysOfWeek: z.array(DayOfWeek),
    monthlyMode: MonthlyMode.nullable(),
    monthlyDayOfMonth: z.number().int().nullable(),
    monthlyDaysBeforeEnd: z.number().int().nullable(),
    monthlyWeekday: DayOfWeek.nullable(),
    monthlyNth: z.number().int().nullable(),
    intradayIntervalMinutes: z.number().int().nullable(),
    intradayStartHour: Hour.nullable(),
    intradayEndHour: Hour.nullable(),
    chainAfterConnectionId: z.number().int().nullable(),
    chainAfterAppId: z.string().nullable(),
    chainAfterAppName: z.string().nullable(),
    // createdBy is portal User.id (CUID) — not necessarily UUID.
    createdBy: z.string(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=schedule.js.map